import React, { Component } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./InstagramAutoSetupMarketingAnalysisModal.css";

const NAV = [
  ["brand", "01 브랜드 아이덴티티"],
  ["market", "02 시장 및 타깃 분석"],
  ["strategy", "03 마케팅 추천 전략"],
  ["roadmap", "04 8주 실행 계획"],
];

const get = (o, path, fallback = "") => path.reduce((v, k) => v == null ? undefined : v[k], o) ?? fallback;
const arr = (value) => Array.isArray(value) ? value : [];
const color = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || "") ? value : fallback;
// 2주 단위 표에서도 각 주차(예: 3주차와 4주차)를 개별 항목으로 표시한다.
// GPT가 줄바꿈을 넣지 않아도 주차 표기를 기준으로 분리하고, 기존 글머리표는 중복 생성하지 않는다.
const formatRoadmapActions = (value, period) => {
  const normalized = String(value ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\r\n?|[\u2028\u2029]/g, "\n");
  const match = String(period).match(/([1-8])\s*[-~–〜]\s*([1-8])\s*주차/);
  const weeks = match ? [Number(match[1]), Number(match[2])] : [];
  const weekPattern = weeks.length
    ? new RegExp(`(^|[\\s,;|/]+)(?:[-–—•*][ \\t]*)?(${weeks.join("|")})\\s*주차(?=$|[\\s:：,.!?()])`, "gm")
    : null;
  // '3주차 내용 4주차 내용' -> '3주차 내용\n4주차 내용'
  const separated = weekPattern
    ? normalized.replace(weekPattern, (_whole, _prefix, week) => `\n${week}주차`)
    : normalized;
  return separated.split("\n")
    .map(line => line.trim().replace(/^(?:[-–—•*]\s*)+/, "").trim())
    .filter(Boolean)
    .map(line => `- ${line}`)
    .join("\n");
};

// 오래된 7/8행 데이터와 신규 4행, 일부 누락된 응답을 모두 2주 단위 4행으로 표시한다.
const normalizeRoadmapGroups = (value) => {
  const incoming = Array.isArray(value) ? value : [];
  const periods = ["1-2주차", "3-4주차", "5-6주차", "7-8주차"];
  const result = periods.map(period => ({ period, goal: "", actions: "", kpis: "" }));
  const text = v => v == null ? "" : typeof v === "object" ? (Array.isArray(v) ? v.join(", ") : JSON.stringify(v)) : String(v);
  const join = (before, part) => !part ? before : before.includes(part) ? before : before ? `${before}\n${part}` : part;
  incoming.forEach((raw, index) => {
    const item = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : { actions: raw };
    const period = text(item.period || item.week || item.week_number || "");
    const found = period.match(/(?:^|[^0-9])([1-8])\s*(?:[-~–〜]\s*([1-8]))?\s*주/);
    const grouped = Boolean(found && found[2]);
    let week = found ? Number(found[1]) : /^[1-8]$/.test(period) ? Number(period) : -1;
    if (week < 1) week = incoming.length <= 4 ? index * 2 + 1 : incoming.length === 7 ? index + 2 : index + 1;
    if (week < 1 || week > 8) return;
    const individual = incoming.length > 4 || (!grouped && /^[1-8](?:주차)?$/.test(period));
    if (week === 1 && individual) return;
    const target = result[Math.floor((week - 1) / 2)];
    const goal = text(item.goal || item.objective || item.title);
    const kpis = text(item.kpis || item.kpi || item.metric || item.metrics);
    const rawActions = item.actions || item.action || item.strategy || item.content || item.description;
    const actions = (Array.isArray(rawActions) ? rawActions.map(text).join("\n") : text(rawActions))
      .replace(/1주차 첫 콘텐츠로 서비스 소개 피드 1건 발행/g, "")
      .replace(/첫 콘텐츠로 서비스 소개 피드 1건 발행/g, "")
      .replace(/^[ \t]*[-•][ \t]*(?:\r?\n|$)/gm, "").trim();
    // 이미 주차가 포함된 텍스트에 다시 "3주차"가 붙는 문제를 방지한다.
    const actionBody = individual && week >= 2
      ? actions.replace(/^[-–—•*]\s*/, "").replace(new RegExp(`^${week}\\s*주차\\s*[:：]?\\s*`), "")
      : actions;
    const prefixedActions = actionBody && individual && week >= 2 ? `${week}주차 ${actionBody}` : actionBody;
    target.goal = join(target.goal, goal);
    target.actions = join(target.actions, prefixedActions);
    target.kpis = join(target.kpis, kpis);
  });
  result[0].goal ||= "계정 세팅 및 정보성 기틀 마련";
  result[0].actions = "- 1주차 첫 콘텐츠로 서비스 소개 피드 1건 발행" +
    (result[0].actions ? `\n${result[0].actions}` : "");
  result[0].kpis ||= "서비스 소개 피드 발행 완료 여부";
  result.forEach(group => { group.actions = formatRoadmapActions(group.actions, group.period); });
  return result;
};


// 원본 HTML의 아이콘과 같은 역할을 수행하며 별도의 Font Awesome 설치가 필요 없습니다.
function ReportIcon({ name, size = 20 }) {
  const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  const paths = {
    building: <><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 21v-4h6v4M8 7h1m6 0h1M8 11h1m6 0h1"/></>,
    box: <><rect x="4" y="4" width="16" height="16" rx="3"/><path d="m4 9 8 4 8-4M12 13v7"/></>,
    users: <><circle cx="9" cy="8" r="3"/><path d="M3 20v-2a6 6 0 0 1 12 0v2M17 6a3 3 0 0 1 0 6m4 8v-2a6 6 0 0 0-4-5.65"/></>,
    target: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/></>,
    user: <><circle cx="12" cy="8" r="3.5"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></>,
    store: <><path d="M3 10h18l-2-6H5l-2 6Zm2 0v10h14V10M9 20v-6h6v6"/><path d="M3 10c0 2 3 3 4.5 0 1.5 3 4.5 3 4.5 0 1.5 3 4.5 3 4.5 0 1.5 3 4.5 2 4.5 0"/></>,
    briefcase: <><rect x="3" y="7" width="18" height="14" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18m-11-2v4h4v-4"/></>,
    instagram: <><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.7" r="1" fill="currentColor" stroke="none"/></>,
    threads: <><circle cx="12" cy="12" r="9"/><path d="M16.2 8.7c-1-1.1-2.3-1.6-4-1.6-3 0-4.8 2-4.8 5s1.8 4.8 4.6 4.8c2.4 0 4.1-1.4 4.1-3.4 0-1.5-1.3-2.5-3.3-2.5-1.8 0-3.3.8-3.3 2.2 0 1.1.8 1.7 1.8 1.7 1.6 0 2.7-1.6 2.7-4.2 0-2-.6-3.7-2-4.6"/></>,
    youtube: <><rect x="2" y="5" width="20" height="14" rx="4"/><path d="m10 9 5 3-5 3V9Z"/></>,
    globe: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c-4 4.5-4 13.5 0 18M12 3c4 4.5 4 13.5 0 18"/></>,
    download: <><path d="M12 3v12m-4-4 4 4 4-4M4 17v4h16v-4"/></>,
    close: <><path d="M5 5 19 19M19 5 5 19"/></>,
  };
  return <svg {...props}>{paths[name] || paths.globe}</svg>;
}

// 외부 스타일시트가 앱 전체 CSS를 변경하지 않도록 승인된 폰트 CDN만 로드합니다.
const getVerifiedFontCssUrl = (value) => {
  try {
    if (!value || String(value).length > 2000) return "";
    const url = new URL(String(value));
    const host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" || url.username || url.password) return "";
    const hosts = ["fonts.googleapis.com", "cdn.jsdelivr.net", "cdnjs.cloudflare.com",
      "fonts.cdnfonts.com", "cdn.spoqa.com", "use.typekit.net"];
    if (!hosts.includes(host)) return "";
    const path = url.pathname.toLowerCase();
    const hint = `${path} ${url.search}`.toLowerCase();
    if (host === "fonts.googleapis.com" && path.startsWith("/css")) return url.href;
    if (host === "use.typekit.net" && path.endsWith(".css")) return url.href;
    if (path.endsWith(".css") && /(font|pretendard|noto|nanum|spoqa|suite|wanted|gmarket|paperlogy|suit|woff|typeface|typography)/.test(hint)) return url.href;
  } catch (_) { /* 비정상 URL은 무시 */ }
  return "";
};
const getPreviewFontFamily = (value) => {
  const family = String(value || "").trim();
  return family.length <= 80 && /^[\p{L}\p{N} _+\-]+$/u.test(family) ? family : "";
};


/**
 * 웹폰트 CDN 대체 목록: 사이트에서 font_css_url을 수집하지 못해도 미리보기에서 폰트를 로드합니다.
 * Google Fonts: https://fonts.google.com/ , Pretendard/SUIT/SUITE: 각 배포처 공식 CDN
 * CSS URL만 저장하며 폰트 파일을 앱에 포함하지 않습니다.
 */
const FONT_CDN_MAP = {
  // UI용 고딕
  "Pretendard": "https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard-dynamic-subset.min.css",
  "Pretendard Variable": "https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css",
  "SUIT": "https://cdn.jsdelivr.net/gh/sun-typeface/SUIT@2/fonts/static/woff2/SUIT.css",
  "SUITE": "https://cdn.jsdelivr.net/gh/sun-typeface/SUITE@2/fonts/static/woff2/SUITE.css",
  // 고딕 및 제목
  "Noto Sans KR": "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;600;700&display=swap",
  "IBM Plex Sans KR": "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+KR:wght@400;500;600;700&display=swap",
  "Gothic A1": "https://fonts.googleapis.com/css2?family=Gothic+A1:wght@400;500;600;700&display=swap",
  "Nanum Gothic": "https://fonts.googleapis.com/css2?family=Nanum+Gothic:wght@400;700;800&display=swap",
  "Gowun Dodum": "https://fonts.googleapis.com/css2?family=Gowun+Dodum&display=swap",
  "Asta Sans": "https://fonts.googleapis.com/css2?family=Asta+Sans:wght@400;500;700&display=swap",
  "Do Hyeon": "https://fonts.googleapis.com/css2?family=Do+Hyeon&display=swap",
  "Jua": "https://fonts.googleapis.com/css2?family=Jua&display=swap",
  "Black Han Sans": "https://fonts.googleapis.com/css2?family=Black+Han+Sans&display=swap",
  "Sunflower": "https://fonts.googleapis.com/css2?family=Sunflower:wght@300;500;700&display=swap",
  "Dongle": "https://fonts.googleapis.com/css2?family=Dongle:wght@300;400;700&display=swap",
  "Gasoek One": "https://fonts.googleapis.com/css2?family=Gasoek+One&display=swap",
  // 명조
  "Noto Serif KR": "https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;500;600;700&display=swap",
  "Nanum Myeongjo": "https://fonts.googleapis.com/css2?family=Nanum+Myeongjo:wght@400;700;800&display=swap",
  "Gowun Batang": "https://fonts.googleapis.com/css2?family=Gowun+Batang:wght@400;700&display=swap",
  "Hahmlet": "https://fonts.googleapis.com/css2?family=Hahmlet:wght@400;500;700&display=swap",
  "Song Myung": "https://fonts.googleapis.com/css2?family=Song+Myung&display=swap",
  "Diphylleia": "https://fonts.googleapis.com/css2?family=Diphylleia&display=swap",
  "Grandiflora One": "https://fonts.googleapis.com/css2?family=Grandiflora+One&display=swap",
  // 손글씨 및 개성체
  "Nanum Pen Script": "https://fonts.googleapis.com/css2?family=Nanum+Pen+Script&display=swap",
  "Nanum Brush Script": "https://fonts.googleapis.com/css2?family=Nanum+Brush+Script&display=swap",
  "Gaegu": "https://fonts.googleapis.com/css2?family=Gaegu:wght@300;400;700&display=swap",
  "Gamja Flower": "https://fonts.googleapis.com/css2?family=Gamja+Flower&display=swap",
  "Hi Melody": "https://fonts.googleapis.com/css2?family=Hi+Melody&display=swap",
  "Poor Story": "https://fonts.googleapis.com/css2?family=Poor+Story&display=swap",
  "Yeon Sung": "https://fonts.googleapis.com/css2?family=Yeon+Sung&display=swap",
  "East Sea Dokdo": "https://fonts.googleapis.com/css2?family=East+Sea+Dokdo&display=swap",
  "Cute Font": "https://fonts.googleapis.com/css2?family=Cute+Font&display=swap",
  "Single Day": "https://fonts.googleapis.com/css2?family=Single+Day&display=swap",
  "Dokdo": "https://fonts.googleapis.com/css2?family=Dokdo&display=swap",
  "Gugi": "https://fonts.googleapis.com/css2?family=Gugi&display=swap",
  "Kirang Haerang": "https://fonts.googleapis.com/css2?family=Kirang+Haerang&display=swap",
  "Stylish": "https://fonts.googleapis.com/css2?family=Stylish&display=swap",
  "Black And White Picture": "https://fonts.googleapis.com/css2?family=Black+And+White+Picture&display=swap",
  "Moirai One": "https://fonts.googleapis.com/css2?family=Moirai+One&display=swap",
  "Bagel Fat One": "https://fonts.googleapis.com/css2?family=Bagel+Fat+One&display=swap",
  "Orbit": "https://fonts.googleapis.com/css2?family=Orbit&display=swap",
  // 코딩
  "Nanum Gothic Coding": "https://fonts.googleapis.com/css2?family=Nanum+Gothic+Coding:wght@400;700&display=swap",
};

/** GPT가 반환하는 한글 표기와 공백/대소문자 차이를 표준 font-family 이름으로 변환합니다. */
const normalizeFontKey = (value) => String(value || "").normalize("NFKC").toLowerCase().replace(/[\s_+\-]+/g, "").trim();
const FONT_NAME_ALIASES = {
  "노토산스": "Noto Sans KR",
  "노토산스kr": "Noto Sans KR",
  "노토산스코리아": "Noto Sans KR",
  "본고딕": "Noto Sans KR",
  "노토세리프": "Noto Serif KR",
  "노토세리프kr": "Noto Serif KR",
  "본명조": "Noto Serif KR",
  "프리텐다드": "Pretendard",
  "프리텐다드variable": "Pretendard Variable",
  "프리텐다드가변": "Pretendard Variable",
  "수트": "SUIT",
  "스위트": "SUITE",
  "아이비엠플렉스산스": "IBM Plex Sans KR",
  "나눔고딕": "Nanum Gothic",
  "나눔명조": "Nanum Myeongjo",
  "나눔고딕코딩": "Nanum Gothic Coding",
  "나눔손글씨펜": "Nanum Pen Script",
  "나눔손글씨붓": "Nanum Brush Script",
  "고운돋움": "Gowun Dodum",
  "고운바탕": "Gowun Batang",
  "고딕a1": "Gothic A1",
  "검은고딕": "Black Han Sans",
  "도현": "Do Hyeon",
  "주아": "Jua",
  "해바라기": "Sunflower",
  "동글": "Dongle",
  "송명": "Song Myung",
  "함렛": "Hahmlet",
  "감자꽃마을": "Gamja Flower",
  "개구쟁이": "Gaegu",
  "하이멜로디": "Hi Melody",
  "연성": "Yeon Sung",
  "기랑해랑": "Kirang Haerang",
  "구기": "Gugi",
  "싱글데이": "Single Day",
  "배걸팻원": "Bagel Fat One",
  "독도": "Dokdo",
  "동해독도": "East Sea Dokdo",
};

// 수집 URL이 잘못되어도 미리보기에 영향을 주지 않습니다.
// 사용 가능한 웹폰트: 홈페이지 CDN -> 폰트명에 대응하는 CDN -> Pretendard.
const resolveReportFontCandidates = (brand = {}) => {
  const originalFamily = getPreviewFontFamily(brand.font_family);
  const key = normalizeFontKey(originalFamily);
  const canonical = Object.keys(FONT_CDN_MAP).find(name => normalizeFontKey(name) === key)
    || FONT_NAME_ALIASES[key] || "";
  const candidates = [];
  const add = (fontFamily, url, source) => {
    const validUrl = getVerifiedFontCssUrl(url);
    if (fontFamily && validUrl && !candidates.some(item => item.fontFamily === fontFamily && item.url === validUrl)) {
      candidates.push({ fontFamily, url: validUrl, source });
    }
  };
  add(originalFamily, brand.font_css_url, "website");
  if (canonical) add(canonical, FONT_CDN_MAP[canonical], "registry");
  add("Pretendard", FONT_CDN_MAP.Pretendard, "default");
  return candidates;
};

// 실패한 폰트 요청이 화면 렌더링이나 PDF 다운로드를 장시간 막지 않도록 제한합니다.
const REPORT_CSS_TIMEOUT_MS = 2500;
const REPORT_FONT_TIMEOUT_MS = 2500;
const REPORT_PDF_FONT_TIMEOUT_MS = 6500;
const REPORT_PDF_LOGO_URL = "https://robomd.io/images/home/logo.svg";
const REPORT_PDF_LOGO_TIMEOUT_MS = 10000;

// SVG 로고를 PDF에 넣을 수 있는 PNG 데이터로 변환합니다.
// 외부 이미지가 캔버스를 오염시키지 않도록 CORS를 사용합니다.
const loadReportPdfLogo = () => new Promise((resolve, reject) => {
  const image = new Image();
  let settled = false;
  const finish = (error, result) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    image.onload = null;
    image.onerror = null;
    if (error) reject(error);
    else resolve(result);
  };
  const timer = setTimeout(() => {
    finish(new Error("로보엠디 로고를 불러오는 데 시간이 초과되었습니다."));
  }, REPORT_PDF_LOGO_TIMEOUT_MS);

  image.crossOrigin = "anonymous";
  image.onload = () => {
    try {
      const originalWidth = image.naturalWidth;
      const originalHeight = image.naturalHeight;
      if (!originalWidth || !originalHeight) throw new Error("로고 이미지의 크기를 확인할 수 없습니다.");
      const canvas = document.createElement("canvas");
      // PDF 출력 시 흐릿하지 않도록 SVG를 충분한 해상도로 래스터화합니다.
      canvas.width = 800;
      canvas.height = Math.max(1, Math.round(800 * originalHeight / originalWidth));
      const context = canvas.getContext("2d");
      if (!context) throw new Error("로고 이미지를 변환할 수 없습니다.");
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      finish(null, {
        dataUrl: canvas.toDataURL("image/png"),
        aspectRatio: originalWidth / originalHeight,
      });
    } catch (error) {
      finish(new Error("로보엠디 로고를 PDF에 추가할 수 없습니다. 이미지 서버의 CORS 설정을 확인해 주세요."));
    }
  };
  image.onerror = () => finish(new Error("로보엠디 로고를 불러오지 못했습니다. 이미지 주소와 CORS 설정을 확인해 주세요."));
  image.src = REPORT_PDF_LOGO_URL;
});
const waitWithTimeout = (promise, timeoutMs, fallback = false) => {
  let timer;
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise(resolve => { timer = setTimeout(() => resolve(fallback), timeoutMs); })
  ]).finally(() => clearTimeout(timer));
};

// PDF용 DOM 복제본의 브랜드 로고만 data URL로 고정합니다.
// 이미지가 화면에 표시되어도 html2canvas가 별도로 로드하는 과정에서 누락될 수 있어
// CORS가 허용된 원본을 직접 읽고 디코딩까지 확인한 후 캡처합니다.
const preparePdfBrandLogo = async (captureRoot) => {
  const img = captureRoot.querySelector(".ms-logo");
  if (!img) return;

  const source = img.getAttribute("src");
  if (!source) return;
  const originalSrcset = img.getAttribute("srcset");
  let dataUrlApplied = false;
  const timeoutMs = 15000;
  const fail = () => new Error("브랜드 로고를 PDF에 포함할 수 없습니다. 이미지 로딩 상태를 확인해 주세요.");
  try {
    const response = await waitWithTimeout(
      fetch(source, { mode: "cors", credentials: "omit" }), timeoutMs, null
    );
    if (!response?.ok) throw fail();

    const blob = await waitWithTimeout(response.blob(), timeoutMs, null);
    if (!blob || !blob.type.startsWith("image/")) throw fail();

    const dataUrl = await waitWithTimeout(new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(fail());
      reader.readAsDataURL(blob);
    }), timeoutMs, null);
    if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) throw fail();

    // 화면에서 사용 중인 원본 노드는 변경하지 않고 캡처 복제본에만 적용합니다.
    img.removeAttribute("srcset");
    img.src = dataUrl;
    dataUrlApplied = true;
    img.style.display = "";
    const loaded = await waitWithTimeout(
      typeof img.decode === "function"
        ? img.decode().then(() => true)
        : new Promise(resolve => {
            if (img.complete) { resolve(img.naturalWidth > 0); return; }
            img.addEventListener("load", () => resolve(true), { once: true });
            img.addEventListener("error", () => resolve(false), { once: true });
          }),
      timeoutMs, false
    );
    if (!loaded || img.naturalWidth <= 0) throw fail();
  } catch (error) {
    // 선택 이미지인 브랜드 로고가 실패해도 본문 PDF 다운로드는 계속 진행합니다.
    // data URL 디코딩 실패 시 기존 S3 URL로 되돌려 html2canvas가 다시 시도할 수 있게 합니다.
    if (dataUrlApplied) {
      if (originalSrcset !== null) img.setAttribute("srcset", originalSrcset);
      else img.removeAttribute("srcset");
      img.setAttribute("src", source);
    }
    console.warn("[AUTO_SETUP_MARKETING_REPORT_PDF] 브랜드 로고 준비 실패: 원본 이미지로 캡처를 계속합니다.", error);
    return false;
  }
};

/**
 * 추천 채널별 아이콘 목록. GPT는 기존과 같이 자유 형식 채널명만 반환합니다.
 * 프론트에서 채널명을 표준화해 일치하는 SVG를 선택합니다.
 * Font Awesome Free Brands v6.7.2 SVG path 일부 사용 (Icons: CC BY 4.0)
 * Attribution: Fonticons, Inc. https://fontawesome.com/license/free
 * Brand logos are trademarks of their respective owners; no endorsement implied.
 * 글꼴/외부 CDN/추가 NPM 패키지 없이 화면과 html2canvas PDF에 동일하게 렌더링됩니다.
 */
const SNS_BRAND_SVG_PATHS = {
  "instagram": { viewBox: "0 0 448 512", path: "M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" },
  "threads": { viewBox: "0 0 448 512", path: "M331.5 235.7c2.2 .9 4.2 1.9 6.3 2.8c29.2 14.1 50.6 35.2 61.8 61.4c15.7 36.5 17.2 95.8-30.3 143.2c-36.2 36.2-80.3 52.5-142.6 53h-.3c-70.2-.5-124.1-24.1-160.4-70.2c-32.3-41-48.9-98.1-49.5-169.6V256v-.2C17 184.3 33.6 127.2 65.9 86.2C102.2 40.1 156.2 16.5 226.4 16h.3c70.3 .5 124.9 24 162.3 69.9c18.4 22.7 32 50 40.6 81.7l-40.4 10.8c-7.1-25.8-17.8-47.8-32.2-65.4c-29.2-35.8-73-54.2-130.5-54.6c-57 .5-100.1 18.8-128.2 54.4C72.1 146.1 58.5 194.3 58 256c.5 61.7 14.1 109.9 40.3 143.3c28 35.6 71.2 53.9 128.2 54.4c51.4-.4 85.4-12.6 113.7-40.9c32.3-32.2 31.7-71.8 21.4-95.9c-6.1-14.2-17.1-26-31.9-34.9c-3.7 26.9-11.8 48.3-24.7 64.8c-17.1 21.8-41.4 33.6-72.7 35.3c-23.6 1.3-46.3-4.4-63.9-16c-20.8-13.8-33-34.8-34.3-59.3c-2.5-48.3 35.7-83 95.2-86.4c21.1-1.2 40.9-.3 59.2 2.8c-2.4-14.8-7.3-26.6-14.6-35.2c-10-11.7-25.6-17.7-46.2-17.8H227c-16.6 0-39 4.6-53.3 26.3l-34.4-23.6c19.2-29.1 50.3-45.1 87.8-45.1h.8c62.6 .4 99.9 39.5 103.7 107.7l-.2 .2zm-156 68.8c1.3 25.1 28.4 36.8 54.6 35.3c25.6-1.4 54.6-11.4 59.5-73.2c-13.2-2.9-27.8-4.4-43.4-4.4c-4.8 0-9.6 .1-14.4 .4c-42.9 2.4-57.2 23.2-56.2 41.8l-.1 .1z" },
  "youtube": { viewBox: "0 0 576 512", path: "M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z" },
  "tiktok": { viewBox: "0 0 448 512", path: "M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" },
  "facebook": { viewBox: "0 0 320 512", path: "M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z" },
  "linkedin": { viewBox: "0 0 448 512", path: "M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" },
  "x": { viewBox: "0 0 512 512", path: "M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" },
  "pinterest": { viewBox: "0 0 384 512", path: "M204 6.5C101.4 6.5 0 74.9 0 185.6 0 256 39.6 296 63.6 296c9.9 0 15.6-27.6 15.6-35.4 0-9.3-23.7-29.1-23.7-67.8 0-80.4 61.2-137.4 140.4-137.4 68.1 0 118.5 38.7 118.5 109.8 0 53.1-21.3 152.7-90.3 152.7-24.9 0-46.2-18-46.2-43.8 0-37.8 26.4-74.4 26.4-113.4 0-66.2-93.9-54.2-93.9 25.8 0 16.8 2.1 35.4 9.6 50.7-13.8 59.4-42 147.9-42 209.1 0 18.9 2.7 37.5 4.5 56.4 3.4 3.8 1.7 3.4 6.9 1.5 50.4-69 48.6-82.5 71.4-172.8 12.3 23.4 44.1 36 69.3 36 106.2 0 153.9-103.5 153.9-196.8C384 71.3 298.2 6.5 204 6.5z" },
  "discord": { viewBox: "0 0 640 512", path: "M524.531,69.836a1.5,1.5,0,0,0-.764-.7A485.065,485.065,0,0,0,404.081,32.03a1.816,1.816,0,0,0-1.923.91,337.461,337.461,0,0,0-14.9,30.6,447.848,447.848,0,0,0-134.426,0,309.541,309.541,0,0,0-15.135-30.6,1.89,1.89,0,0,0-1.924-.91A483.689,483.689,0,0,0,116.085,69.137a1.712,1.712,0,0,0-.788.676C39.068,183.651,18.186,294.69,28.43,404.354a2.016,2.016,0,0,0,.765,1.375A487.666,487.666,0,0,0,176.02,479.918a1.9,1.9,0,0,0,2.063-.676A348.2,348.2,0,0,0,208.12,430.4a1.86,1.86,0,0,0-1.019-2.588,321.173,321.173,0,0,1-45.868-21.853,1.885,1.885,0,0,1-.185-3.126c3.082-2.309,6.166-4.711,9.109-7.137a1.819,1.819,0,0,1,1.9-.256c96.229,43.917,200.41,43.917,295.5,0a1.812,1.812,0,0,1,1.924.233c2.944,2.426,6.027,4.851,9.132,7.16a1.884,1.884,0,0,1-.162,3.126,301.407,301.407,0,0,1-45.89,21.83,1.875,1.875,0,0,0-1,2.611,391.055,391.055,0,0,0,30.014,48.815,1.864,1.864,0,0,0,2.063.7A486.048,486.048,0,0,0,610.7,405.729a1.882,1.882,0,0,0,.765-1.352C623.729,277.594,590.933,167.465,524.531,69.836ZM222.491,337.58c-28.972,0-52.844-26.587-52.844-59.239S193.056,219.1,222.491,219.1c29.665,0,53.306,26.82,52.843,59.239C275.334,310.993,251.924,337.58,222.491,337.58Zm195.38,0c-28.971,0-52.843-26.587-52.843-59.239S388.437,219.1,417.871,219.1c29.667,0,53.307,26.82,52.844,59.239C470.715,310.993,447.538,337.58,417.871,337.58Z" },
  "reddit": { viewBox: "0 0 512 512", path: "M373 138.6c-25.2 0-46.3-17.5-51.9-41l0 0c-30.6 4.3-54.2 30.7-54.2 62.4l0 .2c47.4 1.8 90.6 15.1 124.9 36.3c12.6-9.7 28.4-15.5 45.5-15.5c41.3 0 74.7 33.4 74.7 74.7c0 29.8-17.4 55.5-42.7 67.5c-2.4 86.8-97 156.6-213.2 156.6S45.5 410.1 43 323.4C17.6 311.5 0 285.7 0 255.7c0-41.3 33.4-74.7 74.7-74.7c17.2 0 33 5.8 45.7 15.6c34-21.1 76.8-34.4 123.7-36.4l0-.3c0-44.3 33.7-80.9 76.8-85.5C325.8 50.2 347.2 32 373 32c29.4 0 53.3 23.9 53.3 53.3s-23.9 53.3-53.3 53.3zM157.5 255.3c-20.9 0-38.9 20.8-40.2 47.9s17.1 38.1 38 38.1s36.6-9.8 37.8-36.9s-14.7-49.1-35.7-49.1zM395 303.1c-1.2-27.1-19.2-47.9-40.2-47.9s-36.9 22-35.7 49.1c1.2 27.1 16.9 36.9 37.8 36.9s39.3-11 38-38.1zm-60.1 70.8c1.5-3.6-1-7.7-4.9-8.1c-23-2.3-47.9-3.6-73.8-3.6s-50.8 1.3-73.8 3.6c-3.9 .4-6.4 4.5-4.9 8.1c12.9 30.8 43.3 52.4 78.7 52.4s65.8-21.6 78.7-52.4z" },
  "telegram": { viewBox: "0 0 496 512", path: "M248,8C111.033,8,0,119.033,0,256S111.033,504,248,504,496,392.967,496,256,384.967,8,248,8ZM362.952,176.66c-3.732,39.215-19.881,134.378-28.1,178.3-3.476,18.584-10.322,24.816-16.948,25.425-14.4,1.326-25.338-9.517-39.287-18.661-21.827-14.308-34.158-23.215-55.346-37.177-24.485-16.135-8.612-25,5.342-39.5,3.652-3.793,67.107-61.51,68.335-66.746.153-.655.3-3.1-1.154-4.384s-3.59-.849-5.135-.5q-3.283.746-104.608,69.142-14.845,10.194-26.894,9.934c-8.855-.191-25.888-5.006-38.551-9.123-15.531-5.048-27.875-7.717-26.8-16.291q.84-6.7,18.45-13.7,108.446-47.248,144.628-62.3c68.872-28.647,83.183-33.623,92.511-33.789,2.052-.034,6.639.474,9.61,2.885a10.452,10.452,0,0,1,3.53,6.716A43.765,43.765,0,0,1,362.952,176.66Z" },
  "whatsapp": { viewBox: "0 0 448 512", path: "M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" },
  "line": { viewBox: "0 0 512 512", path: "M311 196.8v81.3c0 2.1-1.6 3.7-3.7 3.7h-13c-1.3 0-2.4-.7-3-1.5l-37.3-50.3v48.2c0 2.1-1.6 3.7-3.7 3.7h-13c-2.1 0-3.7-1.6-3.7-3.7V196.9c0-2.1 1.6-3.7 3.7-3.7h12.9c1.1 0 2.4 .6 3 1.6l37.3 50.3V196.9c0-2.1 1.6-3.7 3.7-3.7h13c2.1-.1 3.8 1.6 3.8 3.5zm-93.7-3.7h-13c-2.1 0-3.7 1.6-3.7 3.7v81.3c0 2.1 1.6 3.7 3.7 3.7h13c2.1 0 3.7-1.6 3.7-3.7V196.8c0-1.9-1.6-3.7-3.7-3.7zm-31.4 68.1H150.3V196.8c0-2.1-1.6-3.7-3.7-3.7h-13c-2.1 0-3.7 1.6-3.7 3.7v81.3c0 1 .3 1.8 1 2.5c.7 .6 1.5 1 2.5 1h52.2c2.1 0 3.7-1.6 3.7-3.7v-13c0-1.9-1.6-3.7-3.5-3.7zm193.7-68.1H327.3c-1.9 0-3.7 1.6-3.7 3.7v81.3c0 1.9 1.6 3.7 3.7 3.7h52.2c2.1 0 3.7-1.6 3.7-3.7V265c0-2.1-1.6-3.7-3.7-3.7H344V247.7h35.5c2.1 0 3.7-1.6 3.7-3.7V230.9c0-2.1-1.6-3.7-3.7-3.7H344V213.5h35.5c2.1 0 3.7-1.6 3.7-3.7v-13c-.1-1.9-1.7-3.7-3.7-3.7zM512 93.4V419.4c-.1 51.2-42.1 92.7-93.4 92.6H92.6C41.4 511.9-.1 469.8 0 418.6V92.6C.1 41.4 42.2-.1 93.4 0H419.4c51.2 .1 92.7 42.1 92.6 93.4zM441.6 233.5c0-83.4-83.7-151.3-186.4-151.3s-186.4 67.9-186.4 151.3c0 74.7 66.3 137.4 155.9 149.3c21.8 4.7 19.3 12.7 14.4 42.1c-.8 4.7-3.8 18.4 16.1 10.1s107.3-63.2 146.5-108.2c27-29.7 39.9-59.8 39.9-93.1z" },
  "medium": { viewBox: "0 0 640 512", path: "M180.5,74.262C80.813,74.262,0,155.633,0,256S80.819,437.738,180.5,437.738,361,356.373,361,256,280.191,74.262,180.5,74.262Zm288.25,10.646c-49.845,0-90.245,76.619-90.245,171.095s40.406,171.1,90.251,171.1,90.251-76.619,90.251-171.1H559C559,161.5,518.6,84.908,468.752,84.908Zm139.506,17.821c-17.526,0-31.735,68.628-31.735,153.274s14.2,153.274,31.735,153.274S640,340.631,640,256C640,171.351,625.785,102.729,608.258,102.729Z" },
  "twitch": { viewBox: "0 0 512 512", path: "M391.17,103.47H352.54v109.7h38.63ZM285,103H246.37V212.75H285ZM120.83,0,24.31,91.42V420.58H140.14V512l96.53-91.42h77.25L487.69,256V0ZM449.07,237.75l-77.22,73.12H294.61l-67.6,64v-64H140.14V36.58H449.07Z" },
  "snapchat": { viewBox: "0 0 512 512", path: "M496.926,366.6c-3.373-9.176-9.8-14.086-17.112-18.153-1.376-.806-2.641-1.451-3.72-1.947-2.182-1.128-4.414-2.22-6.634-3.373-22.8-12.09-40.609-27.341-52.959-45.42a102.889,102.889,0,0,1-9.089-16.12c-1.054-3.013-1-4.724-.248-6.287a10.221,10.221,0,0,1,2.914-3.038c3.918-2.591,7.96-5.22,10.7-6.993,4.885-3.162,8.754-5.667,11.246-7.44,9.362-6.547,15.909-13.5,20-21.278a42.371,42.371,0,0,0,2.1-35.191c-6.2-16.318-21.613-26.449-40.287-26.449a55.543,55.543,0,0,0-11.718,1.24c-1.029.224-2.059.459-3.063.72.174-11.16-.074-22.94-1.066-34.534-3.522-40.758-17.794-62.123-32.674-79.16A130.167,130.167,0,0,0,332.1,36.443C309.515,23.547,283.91,17,256,17S202.6,23.547,180,36.443a129.735,129.735,0,0,0-33.281,26.783c-14.88,17.038-29.152,38.44-32.673,79.161-.992,11.594-1.24,23.435-1.079,34.533-1-.26-2.021-.5-3.051-.719a55.461,55.461,0,0,0-11.717-1.24c-18.687,0-34.125,10.131-40.3,26.449a42.423,42.423,0,0,0,2.046,35.228c4.105,7.774,10.652,14.731,20.014,21.278,2.48,1.736,6.361,4.24,11.246,7.44,2.641,1.711,6.5,4.216,10.28,6.72a11.054,11.054,0,0,1,3.3,3.311c.794,1.624.818,3.373-.36,6.6a102.02,102.02,0,0,1-8.94,15.785c-12.077,17.669-29.363,32.648-51.434,44.639C32.355,348.608,20.2,352.75,15.069,366.7c-3.868,10.528-1.339,22.506,8.494,32.6a49.137,49.137,0,0,0,12.4,9.387,134.337,134.337,0,0,0,30.342,12.139,20.024,20.024,0,0,1,6.126,2.741c3.583,3.137,3.075,7.861,7.849,14.78a34.468,34.468,0,0,0,8.977,9.127c10.019,6.919,21.278,7.353,33.207,7.811,10.776.41,22.989.881,36.939,5.481,5.778,1.91,11.78,5.605,18.736,9.92C194.842,480.951,217.707,495,255.973,495s61.292-14.123,78.118-24.428c6.907-4.24,12.872-7.9,18.489-9.758,13.949-4.613,26.163-5.072,36.939-5.481,11.928-.459,23.187-.893,33.206-7.812a34.584,34.584,0,0,0,10.218-11.16c3.434-5.84,3.348-9.919,6.572-12.771a18.971,18.971,0,0,1,5.753-2.629A134.893,134.893,0,0,0,476.02,408.71a48.344,48.344,0,0,0,13.019-10.193l.124-.149C498.389,388.5,500.708,376.867,496.926,366.6Zm-34.013,18.277c-20.745,11.458-34.533,10.23-45.259,17.137-9.114,5.865-3.72,18.513-10.342,23.076-8.134,5.617-32.177-.4-63.239,9.858-25.618,8.469-41.961,32.822-88.038,32.822s-62.036-24.3-88.076-32.884c-31-10.255-55.092-4.241-63.239-9.858-6.609-4.563-1.24-17.211-10.341-23.076-10.739-6.907-24.527-5.679-45.26-17.075-13.206-7.291-5.716-11.8-1.314-13.937,75.143-36.381,87.133-92.552,87.666-96.719.645-5.046,1.364-9.014-4.191-14.148-5.369-4.96-29.189-19.7-35.8-24.316-10.937-7.638-15.748-15.264-12.2-24.638,2.48-6.485,8.531-8.928,14.879-8.928a27.643,27.643,0,0,1,5.965.67c12,2.6,23.659,8.617,30.392,10.242a10.749,10.749,0,0,0,2.48.335c3.6,0,4.86-1.811,4.612-5.927-.768-13.132-2.628-38.725-.558-62.644,2.84-32.909,13.442-49.215,26.04-63.636,6.051-6.932,34.484-36.976,88.857-36.976s82.88,29.92,88.931,36.827c12.611,14.421,23.225,30.727,26.04,63.636,2.071,23.919.285,49.525-.558,62.644-.285,4.327,1.017,5.927,4.613,5.927a10.648,10.648,0,0,0,2.48-.335c6.745-1.624,18.4-7.638,30.4-10.242a27.641,27.641,0,0,1,5.964-.67c6.386,0,12.4,2.48,14.88,8.928,3.546,9.374-1.24,17-12.189,24.639-6.609,4.612-30.429,19.343-35.8,24.315-5.568,5.134-4.836,9.1-4.191,14.149.533,4.228,12.511,60.4,87.666,96.718C468.629,373.011,476.119,377.524,462.913,384.877Z" },
  "blogger": { viewBox: "0 0 448 512", path: "M446.6 222.7c-1.8-8-6.8-15.4-12.5-18.5-1.8-1-13-2.2-25-2.7-20.1-.9-22.3-1.3-28.7-5-10.1-5.9-12.8-12.3-12.9-29.5-.1-33-13.8-63.7-40.9-91.3-19.3-19.7-40.9-33-65.5-40.5-5.9-1.8-19.1-2.4-63.3-2.9-69.4-.8-84.8.6-108.4 10C45.9 59.5 14.7 96.1 3.3 142.9 1.2 151.7.7 165.8.2 246.8c-.6 101.5.1 116.4 6.4 136.5 15.6 49.6 59.9 86.3 104.4 94.3 14.8 2.7 197.3 3.3 216 .8 32.5-4.4 58-17.5 81.9-41.9 17.3-17.7 28.1-36.8 35.2-62.1 4.9-17.6 4.5-142.8 2.5-151.7zm-322.1-63.6c7.8-7.9 10-8.2 58.8-8.2 43.9 0 45.4.1 51.8 3.4 9.3 4.7 13.4 11.3 13.4 21.9 0 9.5-3.8 16.2-12.3 21.6-4.6 2.9-7.3 3.1-50.3 3.3-26.5.2-47.7-.4-50.8-1.2-16.6-4.7-22.8-28.5-10.6-40.8zm191.8 199.8l-14.9 2.4-77.5.9c-68.1.8-87.3-.4-90.9-2-7.1-3.1-13.8-11.7-14.9-19.4-1.1-7.3 2.6-17.3 8.2-22.4 7.1-6.4 10.2-6.6 97.3-6.7 89.6-.1 89.1-.1 97.6 7.8 12.1 11.3 9.5 31.2-4.9 39.4z" },
  "wordpress": { viewBox: "0 0 512 512", path: "M256 8C119.3 8 8 119.2 8 256c0 136.7 111.3 248 248 248s248-111.3 248-248C504 119.2 392.7 8 256 8zM33 256c0-32.3 6.9-63 19.3-90.7l106.4 291.4C84.3 420.5 33 344.2 33 256zm223 223c-21.9 0-43-3.2-63-9.1l66.9-194.4 68.5 187.8c.5 1.1 1 2.1 1.6 3.1-23.1 8.1-48 12.6-74 12.6zm30.7-327.5c13.4-.7 25.5-2.1 25.5-2.1 12-1.4 10.6-19.1-1.4-18.4 0 0-36.1 2.8-59.4 2.8-21.9 0-58.7-2.8-58.7-2.8-12-.7-13.4 17.7-1.4 18.4 0 0 11.4 1.4 23.4 2.1l34.7 95.2L200.6 393l-81.2-241.5c13.4-.7 25.5-2.1 25.5-2.1 12-1.4 10.6-19.1-1.4-18.4 0 0-36.1 2.8-59.4 2.8-4.2 0-9.1-.1-14.4-.3C109.6 73 178.1 33 256 33c58 0 110.9 22.2 150.6 58.5-1-.1-1.9-.2-2.9-.2-21.9 0-37.4 19.1-37.4 39.6 0 18.4 10.6 33.9 21.9 52.3 8.5 14.8 18.4 33.9 18.4 61.5 0 19.1-7.3 41.2-17 72.1l-22.2 74.3-80.7-239.6zm81.4 297.2l68.1-196.9c12.7-31.8 17-57.2 17-79.9 0-8.2-.5-15.8-1.5-22.9 17.4 31.8 27.3 68.2 27.3 107 0 82.3-44.6 154.1-110.9 192.7z" },
  "tumblr": { viewBox: "0 0 320 512", path: "M309.8 480.3c-13.6 14.5-50 31.7-97.4 31.7-120.8 0-147-88.8-147-140.6v-144H17.9c-5.5 0-10-4.5-10-10v-68c0-7.2 4.5-13.6 11.3-16 62-21.8 81.5-76 84.3-117.1.8-11 6.5-16.3 16.1-16.3h70.9c5.5 0 10 4.5 10 10v115.2h83c5.5 0 10 4.4 10 9.9v81.7c0 5.5-4.5 10-10 10h-83.4V360c0 34.2 23.7 53.6 68 35.8 4.8-1.9 9-3.2 12.7-2.2 3.5.9 5.8 3.4 7.4 7.9l22 64.3c1.8 5 3.3 10.6-.4 14.5z" },
  "wechat": { viewBox: "0 0 576 512", path: "M385.2 167.6c6.4 0 12.6.3 18.8 1.1C387.4 90.3 303.3 32 207.7 32 100.5 32 13 104.8 13 197.4c0 53.4 29.3 97.5 77.9 131.6l-19.3 58.6 68-34.1c24.4 4.8 43.8 9.7 68.2 9.7 6.2 0 12.1-.3 18.3-.8-4-12.9-6.2-26.6-6.2-40.8-.1-84.9 72.9-154 165.3-154zm-104.5-52.9c14.5 0 24.2 9.7 24.2 24.4 0 14.5-9.7 24.2-24.2 24.2-14.8 0-29.3-9.7-29.3-24.2.1-14.7 14.6-24.4 29.3-24.4zm-136.4 48.6c-14.5 0-29.3-9.7-29.3-24.2 0-14.8 14.8-24.4 29.3-24.4 14.8 0 24.4 9.7 24.4 24.4 0 14.6-9.6 24.2-24.4 24.2zM563 319.4c0-77.9-77.9-141.3-165.4-141.3-92.7 0-165.4 63.4-165.4 141.3S305 460.7 397.6 460.7c19.3 0 38.9-5.1 58.6-9.9l53.4 29.3-14.8-48.6C534 402.1 563 363.2 563 319.4zm-219.1-24.5c-9.7 0-19.3-9.7-19.3-19.6 0-9.7 9.7-19.3 19.3-19.3 14.8 0 24.4 9.7 24.4 19.3 0 10-9.7 19.6-24.4 19.6zm107.1 0c-9.7 0-19.3-9.7-19.3-19.6 0-9.7 9.7-19.3 19.3-19.3 14.5 0 24.4 9.7 24.4 19.3.1 10-9.9 19.6-24.4 19.6z" },
  "messenger": { viewBox: "0 0 512 512", path: "M256.55 8C116.52 8 8 110.34 8 248.57c0 72.3 29.71 134.78 78.07 177.94 8.35 7.51 6.63 11.86 8.05 58.23A19.92 19.92 0 0 0 122 502.31c52.91-23.3 53.59-25.14 62.56-22.7C337.85 521.8 504 423.7 504 248.57 504 110.34 396.59 8 256.55 8zm149.24 185.13l-73 115.57a37.37 37.37 0 0 1-53.91 9.93l-58.08-43.47a15 15 0 0 0-18 0l-78.37 59.44c-10.46 7.93-24.16-4.6-17.11-15.67l73-115.57a37.36 37.36 0 0 1 53.91-9.93l58.06 43.46a15 15 0 0 0 18 0l78.41-59.38c10.44-7.98 24.14 4.54 17.09 15.62z" },
};

// 리스트 순서가 매칭 우선순위입니다. 더 구체적인 플랫폼을 일반 블로그/커뮤니티보다 먼저 배치합니다.
const SNS_CHANNEL_ICON_LIST = [
  { key: "naver-blog", label: "네이버 블로그", color: "#03C75A", aliases: ["네이버 블로그", "네이버블로그", "naver blog"] },
  { key: "naver-cafe", label: "네이버 카페", color: "#03C75A", aliases: ["네이버 카페", "네이버카페", "naver cafe"] },
  { key: "naver-post", label: "네이버 포스트", color: "#03C75A", aliases: ["네이버 포스트", "네이버포스트", "naver post"] },
  { key: "kakao-channel", label: "카카오톡 채널", color: "#391B1B", aliases: ["카카오톡 채널", "카카오톡채널", "카카오 채널", "카카오채널", "kakaotalk channel", "kakao channel", "카카오톡", "kakaotalk"] },
  { key: "kakao-story", label: "카카오스토리", color: "#391B1B", aliases: ["카카오스토리", "kakaostory", "kakao story"] },
  { key: "instagram", label: "인스타그램", color: "#E4405F", aliases: ["인스타그램", "instagram", "인스타", "reels", "릴스"] },
  { key: "threads", label: "스레드", color: "#111111", aliases: ["스레드", "threads", "threads.net"] },
  { key: "youtube", label: "유튜브", color: "#FF0033", aliases: ["유튜브", "유투브", "youtube", "youtu.be", "쇼츠", "shorts"] },
  { key: "tiktok", label: "틱톡", color: "#111111", aliases: ["틱톡", "tiktok", "tik tok"] },
  { key: "facebook", label: "페이스북", color: "#1877F2", aliases: ["페이스북", "facebook", "fb page"] },
  { key: "linkedin", label: "링크드인", color: "#0A66C2", aliases: ["링크드인", "linkedin", "linked in"] },
  { key: "x", label: "X (구 트위터)", color: "#111111", aliases: ["트위터", "twitter", "x.com", "x (", "x("] },
  { key: "pinterest", label: "핀터레스트", color: "#E60023", aliases: ["핀터레스트", "pinterest"] },
  { key: "discord", label: "디스코드", color: "#5865F2", aliases: ["디스코드", "discord"] },
  { key: "reddit", label: "레딧", color: "#FF4500", aliases: ["레딧", "reddit"] },
  { key: "telegram", label: "텔레그램", color: "#229ED9", aliases: ["텔레그램", "telegram"] },
  { key: "whatsapp", label: "왓츠앱", color: "#25D366", aliases: ["왓츠앱", "whatsapp", "whats app"] },
  { key: "line", label: "라인", color: "#06C755", aliases: ["라인 공식계정", "라인 채널", "라인 메신저", "line official", "line app"] },
  { key: "medium", label: "미디엄", color: "#111111", aliases: ["미디엄", "medium.com", "medium blog"] },
  { key: "twitch", label: "트위치", color: "#9146FF", aliases: ["트위치", "twitch"] },
  { key: "snapchat", label: "스냅챗", color: "#B8860B", aliases: ["스냅챗", "snapchat"] },
  { key: "blogger", label: "Blogger", color: "#F57D00", aliases: ["구글 블로거", "google blogger", "blogger", "블로거"] },
  { key: "wordpress", label: "워드프레스", color: "#21759B", aliases: ["워드프레스", "wordpress"] },
  { key: "tumblr", label: "텀블러", color: "#36465D", aliases: ["텀블러", "tumblr"] },
  { key: "wechat", label: "위챗", color: "#07C160", aliases: ["위챗", "wechat", "weixin"] },
  { key: "messenger", label: "메신저", color: "#0084FF", aliases: ["페이스북 메신저", "facebook messenger", "messenger"] },
  { key: "tech-blog", label: "기술 블로그", color: "#2563EB", aliases: ["기술 블로그", "기술블로그", "개발 블로그", "개발블로그", "tech blog", "engineering blog", "dev blog", "티스토리", "tistory", "velog", "벨로그", "브런치", "brunch story"] },
  { key: "business-profile", label: "비즈니스 프로필", color: "#4285F4", aliases: ["구글 비즈니스 프로필", "google business profile", "google business", "구글 지도"] },
  { key: "blog", label: "블로그", color: "#2563EB", aliases: ["블로그", "blog"] },
  { key: "community", label: "커뮤니티", color: "#64748B", aliases: ["커뮤니티", "community", "카페", "forum", "포럼", "band", "네이버 밴드", "밴드"] },
  { key: "website", label: "웹사이트", color: "#3B82F6", aliases: ["웹사이트", "홈페이지", "website", "landing page", "랜딩페이지", "웹 페이지"] },
];

const normalizeSnsChannelName = (value) => String(value || "").normalize("NFKC").toLowerCase();
const matchesSnsChannelAlias = (name, alias) => {
  const candidate = alias.toLowerCase();
  // 'line'은 online과 혼동하지 않도록, X는 다른 영단어에 포함된 x와 혼동하지 않도록 단어 검사.
  if (candidate === "line") return /(?:^|[^a-z])line(?:$|[^a-z])/i.test(name);
  return name.includes(candidate);
};
const resolveSnsChannelIcon = (name) => {
  const value = normalizeSnsChannelName(name);
  if (/^\s*x\s*$/i.test(value)) return SNS_CHANNEL_ICON_LIST.find(item => item.key === "x");
  if (/^\s*라인\s*$/.test(value) || /(?:^|[^a-z])line(?:$|[^a-z])/i.test(value)) return SNS_CHANNEL_ICON_LIST.find(item => item.key === "line");
  return SNS_CHANNEL_ICON_LIST.find(item => item.aliases.some(alias => matchesSnsChannelAlias(value, alias))) ||
    { key: "globe", label: "기타 채널", color: "#3B82F6" };
};

function SnsChannelIcon({ icon, size = 30 }) {
  const brand = SNS_BRAND_SVG_PATHS[icon.key];
  if (brand) {
    return <svg width={size} height={size} viewBox={brand.viewBox} fill="currentColor" aria-hidden="true" focusable="false">
      <path d={brand.path}/>
    </svg>;
  }
  if (["naver-blog", "naver-cafe", "naver-post"].includes(icon.key)) {
    return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="22" height="22" rx="4" fill="#03C75A"/>
      {icon.key === "naver-blog" ? (
        <path fill="#FFFFFF" d="M7.2 4.5h2.6v6.1c.7-.9 1.6-1.3 2.9-1.3 2.8 0 4.8 2.2 4.8 5.1s-2 5.1-4.8 5.1c-1.4 0-2.3-.5-3-1.5v1.2H7.2zm5.2 7.3c-1.5 0-2.6 1.1-2.6 2.6s1.1 2.6 2.6 2.6 2.6-1.1 2.6-2.6-1.1-2.6-2.6-2.6"/>
      ) : icon.key === "naver-post" ? (
        <path d="M7 5.5h10v13H7zM9.3 9h5.4M9.3 12h5.4M9.3 15h3" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round"/>
      ) : (
        <path d="M6.4 18V6l5.1 7.3V6h6.1v12h-3.4l-4.9-7V18z" fill="#FFFFFF"/>
      )}
    </svg>;
  }
  if (icon.key === "kakao-channel" || icon.key === "kakao-story") {
    return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="22" height="22" rx="5" fill="#FEE500"/>
      <path d="M12 5.1c-4.5 0-8.1 2.7-8.1 6 0 2.1 1.4 4 3.7 5.1l-.8 3.5 4-2.2c.4.1.8.1 1.2.1 4.5 0 8.1-2.7 8.1-6s-3.6-6.5-8.1-6.5z" fill="#391B1B"/>
      {icon.key === "kakao-story" ? (
        <path d="M14.7 9.5c-.7-.5-1.8-.7-2.6-.7-1.4 0-2.4.6-2.4 1.6 0 2.3 4.7.5 4.7 3 0 1.2-1.2 2-2.6 2-1.1 0-2.1-.4-2.7-.9" fill="none" stroke="#FEE500" strokeWidth="1.2" strokeLinecap="round"/>
      ) : (
        <path d="M9.3 9v5m0-2.6 3.8 2.6m-3.8-2.4 3.7-2.6" stroke="#FEE500" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      )}
    </svg>;
  }
  if (icon.key === "tech-blog") {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="3.5" width="19" height="17" rx="2.5"/><path d="M7 9.3 4.5 12 7 14.7M17 9.3l2.5 2.7-2.5 2.7M14.3 7.8l-4.6 8.5"/>
    </svg>;
  }
  if (icon.key === "blog") {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>
    </svg>;
  }
  if (icon.key === "community") return <ReportIcon name="users" size={size}/>;
  if (icon.key === "business-profile") return <ReportIcon name="store" size={size}/>;
  return <ReportIcon name="globe" size={size}/>;
}


class InstagramAutoSetupMarketingAnalysisModal extends Component {
  constructor(props) {
    super(props);
    this.state = { active: "brand", downloading: false, pdfError: "", previewFontFamily: "" };
    this.fontRequestId = 0;
    this.scroller = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("keydown", this.onKeyDown);
    this.oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.loadReportFont();
  }

  componentDidUpdate(prevProps) {
    const prevBrand = get(prevProps.analysisResult, ["analysis", "marketing_report", "brand"], {});
    const nextBrand = get(this.props.analysisResult, ["analysis", "marketing_report", "brand"], {});
    if (prevBrand.font_css_url !== nextBrand.font_css_url || prevBrand.font_family !== nextBrand.font_family) {
      this.loadReportFont();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeyDown);
    document.body.style.overflow = this.oldOverflow || "";
    this.clearReportFont();
  }

  clearReportFont = () => {
    // 이전 요청이 나중에 완료돼도 현재 화면에 영향을 주지 못하도록 무효화합니다.
    this.fontRequestId += 1;
    if (this.reportFontLink?.parentNode) this.reportFontLink.parentNode.removeChild(this.reportFontLink);
    this.reportFontLink = null;
    this.fontLoadPromise = null;
  };

  loadReportFont = () => {
    this.clearReportFont();
    const requestId = this.fontRequestId;
    const brand = get(this.props.analysisResult, ["analysis", "marketing_report", "brand"], {});
    const candidates = resolveReportFontCandidates(brand);
    // 외부 CDN 응답과 관계없이 팝업은 기본 서체로 즉시 렌더링됩니다.
    if (this.state.previewFontFamily) this.setState({ previewFontFamily: "" });
    this.fontLoadPromise = (async () => {
      for (const candidate of candidates) {
        if (requestId !== this.fontRequestId) return false;
        const available = await this.tryReportFontCandidate(candidate, requestId);
        if (requestId !== this.fontRequestId) return false;
        if (!available) continue;
        // 캡처 전에 실제 DOM까지 갱신되도록 setState 콜백을 기다립니다.
        return new Promise(resolve => this.setState(
          { previewFontFamily: candidate.fontFamily }, () => resolve(true)
        ));
      }
      // 모든 CDN이 실패해도 사이트 이용, 이어가기, PDF 생성은 유지됩니다.
      console.warn("[MARKETING_REPORT_FONT] 웹폰트 로드 실패, 브라우저 기본 글꼴 사용");
      return false;
    })().catch(error => {
      console.warn("[MARKETING_REPORT_FONT] 폰트 처리 오류, 기본 글꼴 사용", error);
      return false;
    });
  };

  tryReportFontCandidate = async ({ fontFamily, url, source }, requestId) => {
    let link = null;
    let ownLink = false;
    try {
      const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .find(item => item.href === url);
      if (!existing) {
        link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = url;
        // 교차 출처 폰트 파일에 필요한 CORS는 폰트 제공 서버가 처리합니다.
        link.crossOrigin = "anonymous";
        link.dataset.msReportFont = "true";
        ownLink = true;
        const cssReady = new Promise(resolve => {
          link.onload = () => resolve(true);
          link.onerror = () => resolve(false);
        });
        document.head.appendChild(link);
        const loaded = await waitWithTimeout(cssReady, REPORT_CSS_TIMEOUT_MS);
        if (!loaded || requestId !== this.fontRequestId) {
          console.warn("[MARKETING_REPORT_FONT] CSS 로드 실패 또는 시간 초과", source, url);
          return false;
        }
      }
      const loaded = await this.waitForReportFont(fontFamily);
      if (!loaded) console.warn("[MARKETING_REPORT_FONT] 해당 폰트 글리프 로드 실패", fontFamily, url);
      if (loaded && requestId === this.fontRequestId && ownLink) this.reportFontLink = link;
      return loaded;
    } catch (error) {
      console.warn("[MARKETING_REPORT_FONT] 폰트 후보 처리 실패", fontFamily, error);
      return false;
    } finally {
      // 404, CORS 실패, 응답 지연인 후보는 제거하고 다음 후보로 진행합니다.
      if (ownLink && link !== this.reportFontLink) {
        link.onload = null;
        link.onerror = null;
        if (link.parentNode) link.parentNode.removeChild(link);
      }
    }
  };

  waitForReportFont = async (font) => {
    if (!document.fonts?.load) return false;
    try {
      const faces = await waitWithTimeout(
        document.fonts.load(`16px "${font}"`, "가나 다라마바사 1234567890"),
        REPORT_FONT_TIMEOUT_MS, []
      );
      return Array.isArray(faces) && faces.length > 0;
    } catch (error) {
      console.warn("[MARKETING_REPORT_FONT] 폰트 로드 검증 실패", font, error);
      return false;
    }
  };

  onKeyDown = (event) => {
    if (event.key === "Escape" && !this.state.downloading) this.props.onClose?.();
  };

  goTo = (id) => {
    const scroller = this.scroller.current;
    const element = scroller?.querySelector(`[data-section="${id}"]`);
    if (!element || !scroller) return;
    // 요소의 절대 위치가 아닌 스크롤 영역 기준 상대 위치로 이동합니다.
    const top = element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 38;
    scroller.scrollTo({ top, behavior: "smooth" });
    this.setState({ active: id });
  };

  onContentScroll = () => {
    const scroller = this.scroller.current;
    if (!scroller) return;
    const top = scroller.getBoundingClientRect().top + 155;
    let active = NAV[0][0];
    NAV.forEach(([id]) => {
      const section = scroller.querySelector(`[data-section="${id}"]`);
      if (section && section.getBoundingClientRect().top <= top) active = id;
    });
    if (active !== this.state.active) this.setState({ active });
  };

  // 분석 데이터는 수정하지 않고 화면에 텍스트로만 표시합니다.
  read = (label, path, options = {}) => {
    const value = get(this.props.analysisResult, ["analysis", "marketing_report", ...path]);
    const display = value == null || String(value).trim() === "" ? "" : String(value);
    return (
      <div className={`ms-read-field ${options.className || ""}`}>
        {options.showLabel && label && <span className="ms-field-label">{label}</span>}
        <span className="ms-read-value">{display}</span>
      </div>
    );
  };

  /**
   * 화면과 PDF를 서로 다른 HTML로 구현하지 않습니다.
   * 현재 React 팝업의 보고서 DOM을 그대로 복제해 브라우저 레이아웃으로 렌더링합니다.
   * 상단 제목은 포함하고, 좌측 탐색/하단 버튼은 제외합니다.
   * SVG 아이콘/그리드/색상/폰트/표는 화면과 동일하게 캡처됩니다.
   * 주의: 시각적 일치를 최우선으로 하는 이미지 기반 PDF입니다.
   */
  downloadPdf = async () => {
    if (this.state.downloading) return;
    this.setState({ downloading: true, pdfError: "" });
    let captureRoot = null;
    try {
      const content = this.scroller.current?.querySelector(".ms-content-inner");
      if (!content) throw new Error("PDF로 저장할 보고서 영역을 찾을 수 없습니다.");

      // 스크롤 영역이 아닌 보고서 전체를 복제하여 숨겨진 페이지에 렌더링합니다.
      // 복제된 노드도 현재 React 팝업과 동일한 스타일시트를 사용합니다.
      captureRoot = document.createElement("div");
      captureRoot.className = "ms-overlay ms-pdf-export";
      captureRoot.setAttribute("aria-hidden", "true");
      const header = document.createElement("header");
      header.className = "ms-header";
      const heading = document.createElement("div");
      const eyebrow = document.createElement("span");
      eyebrow.textContent = "AI BUSINESS & MARKETING REPORT";
      const title = document.createElement("h2");
      title.textContent = "기업 분석 및 마케팅 전략";
      heading.append(eyebrow, title);
      header.appendChild(heading);

      const contentArea = document.createElement("div");
      contentArea.className = "ms-content";
      const clonedContent = content.cloneNode(true);
      // 사용자 입력 요소는 없으며, 읽기 전용 보고서 데이터만 복제합니다.
      contentArea.appendChild(clonedContent);
      captureRoot.append(header, contentArea);
      document.body.appendChild(captureRoot);

      // 브랜드 로고를 data URL로 준비하되, 실패하더라도 본문 PDF 생성을 중단하지 않습니다.
      await preparePdfBrandLogo(captureRoot);

      // 사이트 웹폰트가 로드된 후 동일한 서체로 PDF를 캡처합니다.
      if (this.fontLoadPromise) await waitWithTimeout(this.fontLoadPromise, REPORT_PDF_FONT_TIMEOUT_MS);
      // 다른 외부 리소스가 지연되어도 PDF 다운로드가 무한 대기하지 않습니다.
      if (document.fonts?.ready) await waitWithTimeout(document.fonts.ready, 2000);
      const imgs = Array.from(captureRoot.querySelectorAll("img"));
      await Promise.all(imgs.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          const finish = () => { img.removeEventListener("load", finish); img.removeEventListener("error", finish); resolve(); };
          img.addEventListener("load", finish);
          img.addEventListener("error", finish);
          setTimeout(finish, 10000);
        });
      }));

      // JPG 대신 PNG를 사용해 작은 한글 글자와 표 선의 번짐을 줄입니다.
      // 한 페이지를 길게 만들어 카드/표가 페이지 중간에서 끊기지 않게 합니다.
      const canvas = await html2canvas(captureRoot, {
        backgroundColor: "#F8FAFC",
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 15000,
        windowWidth: Math.max(window.innerWidth, 1400),
        scrollX: 0,
        scrollY: -window.scrollY,
      });
      if (!canvas.width || !canvas.height) throw new Error("보고서 이미지 생성에 실패했습니다.");

      // ms-content-inner / ms-overlay의 CSS 여백 때문에 마지막 표 아래에
      // 빈 공간이 캡처될 수 있습니다. 마지막 섹션의 실제 끝을 기준으로 잘라냅니다.
      // 화면 팝업의 레이아웃/CSS에는 영향을 주지 않습니다.
      let reportCanvas = canvas;
      const captureBounds = captureRoot.getBoundingClientRect();
      const lastContent = clonedContent.lastElementChild || clonedContent;
      const lastContentBounds = lastContent.getBoundingClientRect();
      const bottomPaddingPx = 8; // 마지막 본문과 로고 사이 최소 간격
      if (captureBounds.height > 0 && lastContentBounds.height > 0) {
        const captureScaleY = canvas.height / captureBounds.height;
        const contentBottomPx = Math.ceil(
          (lastContentBounds.bottom - captureBounds.top + bottomPaddingPx) * captureScaleY
        );
        if (contentBottomPx > 0 && contentBottomPx < canvas.height) {
          reportCanvas = document.createElement("canvas");
          reportCanvas.width = canvas.width;
          reportCanvas.height = contentBottomPx;
          const reportContext = reportCanvas.getContext("2d");
          if (!reportContext) throw new Error("PDF 보고서 여백을 조정할 수 없습니다.");
          reportContext.drawImage(canvas, 0, 0, canvas.width, contentBottomPx,
            0, 0, canvas.width, contentBottomPx);
        }
      }

      // 팝업 화면의 가로 세로 비율을 유지한 긴 보고서 페이지(메뉴/버튼 제외).
      const pdfWidthMm = 297;
      const reportHeightMm = pdfWidthMm * reportCanvas.height / reportCanvas.width;
      // 하단 로보엠디 로고도 부가 이미지이므로 실패 시 생략하고 PDF는 저장합니다.
      const logo = await loadReportPdfLogo().catch(error => {
        console.warn("[AUTO_SETUP_MARKETING_REPORT_PDF] 하단 로고 준비 실패: 로고 없이 PDF를 저장합니다.", error);
        return null;
      });
      const logoMaxWidthMm = 35;
      const logoMaxHeightMm = 11; // 기존 로고 크기 유지
      const logoWidthMm = logo ? Math.min(logoMaxWidthMm, logoMaxHeightMm * logo.aspectRatio) : 0;
      const logoHeightMm = logo ? logoWidthMm / logo.aspectRatio : 0;
      const logoTopGapMm = 2;
      // 로고 위에는 본문 캡처의 8px 여백도 있으므로, 아래쪽도 같은 시각적 간격으로 맞춥니다.
      const contentPaddingMm = bottomPaddingPx * pdfWidthMm / Math.max(1, captureBounds.width);
      const logoBottomGapMm = logoTopGapMm + contentPaddingMm;
      const footerHeightMm = logo ? logoTopGapMm + logoHeightMm + logoBottomGapMm : logoBottomGapMm;
      const pdfHeightMm = reportHeightMm + footerHeightMm;
      if (!Number.isFinite(pdfHeightMm) || pdfHeightMm <= 0) {
        throw new Error("PDF 페이지 크기를 계산할 수 없습니다.");
      }
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidthMm, pdfHeightMm],
        compress: true,
      });
      pdf.addImage(reportCanvas.toDataURL("image/png"), "PNG", 0, 0, pdfWidthMm, reportHeightMm);

      // PDF 우측 하단에 로고를 본문 가까이 배치합니다.
      pdf.setFillColor(248, 250, 252);
      pdf.rect(0, reportHeightMm, pdfWidthMm, footerHeightMm, "F");
      if (logo) {
        const logoRightMm = 12;
        const logoX = pdfWidthMm - logoRightMm - logoWidthMm;
        const logoY = reportHeightMm + logoTopGapMm;
        try {
          pdf.addImage(logo.dataUrl, "PNG", logoX, logoY, logoWidthMm, logoHeightMm);
        } catch (error) {
          console.warn("[AUTO_SETUP_MARKETING_REPORT_PDF] 하단 로고 삽입 실패: 로고 없이 PDF를 저장합니다.", error);
        }
      }
      pdf.setProperties({ title: "기업 분석 및 마케팅 전략" });
      pdf.save("[로보엠디]SNS마케팅_전략_분석_보고서.pdf");
    } catch (error) {
      console.error("[AUTO_SETUP_MARKETING_REPORT_PDF] 화면 PDF 생성 오류", error);
      this.setState({ pdfError: error?.message || "PDF 생성에 실패했습니다." });
    } finally {
      if (captureRoot?.parentNode) captureRoot.parentNode.removeChild(captureRoot);
      this.setState({ downloading: false });
    }
  };

  renderBrand = (report) => {
    const brand = report.brand || {};
    const src = String(brand.logo_url || get(this.props.analysisResult, ["source", "logo_image_url"], "") || "");
    const swatches = [
      ["메인 컬러", "main", "#00C9D2"],
      ["포인트 컬러", "point", "#0B1B37"],
      ["서브 컬러", "sub", "#EBF8FA"],
      ["배경 컬러", "background", "#FFFFFF"],
    ];
    return (
      <section data-section="brand" className="ms-section" key="brand">
        <h3 className="ms-section-heading"><span className="ms-numeral">1</span>브랜드 아이덴티티</h3>
        <div className="ms-brand-grid">
          <div className="ms-card ms-brand-identity">
            <h4>브랜드 로고 - 서비스명</h4>
            <div className="ms-brand-details">
              {src && <img className="ms-logo" src={src} alt="브랜드 로고" onError={(event) => { event.currentTarget.style.display = "none"; }}/>} 
              {this.read("서비스명", ["brand", "service_name"], { multiline: true, className: "ms-read--brand-title" })}
              {this.read("기업명", ["brand", "company_name"], { multiline: true, className: "ms-read--brand-company" })}
            </div>
          </div>
          <div className="ms-card ms-color-card">
            <h4>브랜드 컬러 시스템</h4>
            <div className="ms-colors">
              {swatches.map(([label, key, fallback]) => {
                const hex = color(brand.colors?.[key], fallback);
                return (
                  <div className="ms-swatch" key={key}>
                    <span className="ms-color-chooser" aria-label={`${label}: ${hex}`}>
                      <span style={{ backgroundColor: hex }}/>
                    </span>
                    <span className="ms-swatch-label">{label}</span>
                    <span className="ms-hex-value">{hex}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="ms-card ms-type-card">
            <h4>폰트 - 타이포그래피</h4>
            <div className="ms-type-details">
              {this.read("추천 폰트", ["brand", "font_family"], { multiline: true, className: "ms-read--font-name" })}
              <div className="ms-font-sample" style={this.state.previewFontFamily
                ? { fontFamily: `"${this.state.previewFontFamily}", sans-serif` }
                : undefined}>
                <span>Aa</span><span>가나 다라마바사<br/>1234567890</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  renderMarket = (report) => {
    const tiles = [
      ["사업 유형", "business_type", "building", "cyan"],
      ["핵심 상품 및 서비스", "core_offer", "box", "blue"],
      ["핵심 타깃", "target", "users", "indigo"],
      ["마케팅 목표", "objective", "target", "purple"],
    ];
    const personaIcons = ["user", "store", "briefcase"];
    const personaColors = ["blue", "green", "purple"];
    return (
      <section data-section="market" className="ms-section" key="market">
        <h3 className="ms-section-heading"><span className="ms-numeral">2</span>시장 및 타깃 분석</h3>
        <div className="ms-market-grid">
          {tiles.map(([title, field, icon, tone]) => (
            <div className="ms-card ms-market-card" key={field}>
              <div className="ms-market-card-head">
                <span className={`ms-market-icon ms-tone-${tone}`}><ReportIcon name={icon} size={22}/></span>
                <h4>{title}</h4>
              </div>
              {this.read(title, ["market", field], { multiline: true, className: "ms-read--market" })}
            </div>
          ))}
        </div>
        <div className="ms-persona-layout">
          <div className="ms-card ms-persona-card">
            <h4>고객 페르소나</h4>
            <div className="ms-personas">
              {arr(report.market?.personas).map((persona, index) => (
                <div key={index} className="ms-soft-card ms-persona-item">
                  <div className="ms-persona-summary">
                    <span className={`ms-persona-icon ms-tone-${personaColors[index % personaColors.length]}`}><ReportIcon name={personaIcons[index % personaIcons.length]} size={22}/></span>
                    <div className="ms-persona-copy">
                      {this.read("페르소나", ["market", "personas", index, "name"], { multiline: true, className: "ms-read--persona-name" })}
                      {this.read("특징 및 고민", ["market", "personas", index, "description"], { multiline: true, className: "ms-read--body" })}
                    </div>
                  </div>
                  {persona?.needs && (
                    <div className="ms-persona-needs">
                      {this.read("핵심 니즈", ["market", "personas", index, "needs"], { multiline: true, showLabel: true, className: "ms-read--needs" })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="ms-card ms-channel-card">
            <h4>추천 SNS 채널</h4>
            <div className="ms-channels">
              {arr(report.market?.channels).map((channel, index) => {
                const icon = resolveSnsChannelIcon(channel?.name);
                return (
                  <div key={index} className="ms-channel-row">
                    <span className={`ms-channel-icon ms-channel-icon--${icon.key}`} style={{ color: icon.color }} title={icon.label}>
                      <SnsChannelIcon icon={icon} size={30}/>
                    </span>
                    <div className="ms-channel-content">
                      <div className="ms-channel-title-row">
                        {this.read("채널", ["market", "channels", index, "name"], { multiline: true, className: "ms-read--channel-name" })}
                        {index === 0 && <span className="ms-primary-badge">주력</span>}
                      </div>
                      {this.read("추천 이유", ["market", "channels", index, "reason"], { multiline: true, className: "ms-read--body" })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    );
  };

  renderStrategy = (report) => (
    <section data-section="strategy" className="ms-section" key="strategy">
      <h3 className="ms-section-heading"><span className="ms-numeral">3</span>AI 추천 전략 - SWOT</h3>
      <div className="ms-two-columns">
        <div className="ms-card ms-strategy-card">
          <h4>SWOT 분석</h4>
          <div className="ms-swot">
            {[["S - 강점", "strength"], ["W - 약점", "weakness"], ["O - 기회", "opportunity"], ["T - 위협", "threat"]].map(([title, field]) => (
              <div className="ms-soft-card ms-swot-cell" key={field}>
                <strong>{title}</strong>
                {this.read(title, ["strategy", "swot", field], { multiline: true, className: "ms-read--body" })}
              </div>
            ))}
          </div>
        </div>
        <div className="ms-card ms-strategy-card">
          <h4>AI 핵심 전략 제안</h4>
          <div className="ms-strategy-highlight">
            {this.read("AI 핵심 전략 제안", ["strategy", "core_strategy"], { multiline: true, className: "ms-read--core-strategy" })}
          </div>
          <h5 className="ms-mix-heading">권장 콘텐츠 믹스 안내</h5>
          <div className="ms-mix-bar">
            {arr(report.strategy?.content_mix).map((item, index) => (
              <div key={index} style={{ width: `${Math.max(0, Math.min(100, Number(item?.percent) || 0))}%`, background: ["#00C9D2", "#60A5FA", "#9CA3AF"][index % 3] }}/>
            ))}
          </div>
          <div className="ms-mix-items">
            {arr(report.strategy?.content_mix).map((item, index) => (
              <div className="ms-mix-item" key={index}>
                <span className="ms-mix-dot" style={{ backgroundColor: ["#00C9D2", "#60A5FA", "#9CA3AF"][index % 3] }}/>
                {this.read("콘텐츠 유형", ["strategy", "content_mix", index, "name"], { multiline: true, className: "ms-read--mix-name" })}
                <div className="ms-mix-percent">
                  <span className="ms-mix-percent-value">{Number(item?.percent) || 0}</span>
                  <span>%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  renderRoadmap = (report) => {
    const groups = normalizeRoadmapGroups(report.roadmap?.items);
    const display = (value) => !value || !String(value).trim() ? "정보 없음" : String(value);
    return (
      <section data-section="roadmap" className="ms-section" key="roadmap">
        <div className="ms-roadmap-heading">
          <h3 className="ms-section-heading"><span className="ms-numeral">4</span>8주 마케팅 실행 계획</h3>
          {/* <div className="ms-roadmap-subtitle">{this.read("전략 방향", ["roadmap", "subtitle"], { className: "ms-read--subtitle" })}</div> */}
        </div>
        <div className="ms-table-scroll">
          <table className="ms-roadmap">
            <colgroup><col style={{width:"15%"}}/><col style={{width:"23%"}}/><col style={{width:"43%"}}/><col style={{width:"19%"}}/></colgroup>
            <thead><tr><th>기간</th><th>핵심 목표</th><th>콘텐츠 및 실행 전략</th><th>확인할 지표</th></tr></thead>
            <tbody>
              {groups.map((group, index) => (
                <tr key={group.period} className={index === 0 ? "ms-fixed-week" : ""}>
                  <td><strong className="ms-week-period">{group.period}</strong></td>
                  <td><span className="ms-read-value ms-group-goal">{display(group.goal)}</span></td>
                  <td><span className="ms-read-value ms-group-actions">{display(group.actions)}</span></td>
                  <td><span className="ms-read-value ms-group-kpis">{display(group.kpis)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  render() {
    const report = get(this.props.analysisResult, ["analysis", "marketing_report"], {});
    return (
      <div className="ms-overlay" role="presentation">
        <div className="ms-modal" role="dialog" aria-modal="true" aria-label="기업 분석 및 마케팅 전략">
          <header className="ms-header">
            <div><span>AI BUSINESS &amp; MARKETING REPORT</span><h2>기업 분석 및 마케팅 전략</h2></div>
            <button type="button" title="닫기" aria-label="닫기" onClick={() => this.props.onClose?.()}><ReportIcon name="close" size={25}/></button>
          </header>
          <div className="ms-main">
            <nav className="ms-nav" aria-label="분석 보고서 메뉴">
              <h4>ANALYSIS &amp; STRATEGY</h4>
              {NAV.map(([id, text]) => <button key={id} className={this.state.active === id ? "active" : ""} type="button" onClick={() => this.goTo(id)}>{text}</button>)}
            </nav>
            <div className="ms-content" ref={this.scroller} onScroll={this.onContentScroll}>
              <div className="ms-content-inner">
                {this.renderBrand(report)}
                {this.renderMarket(report)}
                {this.renderStrategy(report)}
                {this.renderRoadmap(report)}
              </div>
            </div>
          </div>
          <footer className="ms-footer">
            {this.state.pdfError && <div className="ms-error" role="alert">{this.state.pdfError}</div>}
            <button className="ms-pdf-button" type="button" disabled={this.state.downloading} onClick={this.downloadPdf}>
              <ReportIcon name="download" size={17}/>{this.state.downloading ? "PDF 생성 중..." : "PDF 다운로드"}
            </button>
            <button className="ms-continue-button" type="button" disabled={this.state.downloading} onClick={() => this.props.onContinue?.(this.props.analysisResult)}>{"다음"}</button>
          </footer>
        </div>
      </div>
    );
  }
}

export default InstagramAutoSetupMarketingAnalysisModal;
